import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import api from '../../../utils/api';
import { exportDataToCSV } from '../../../utils/exportUtils';

const InventoryStatusTable = () => {
  const navigate = useNavigate();
  const [selectedItems, setSelectedItems] = useState([]);
  const [inventoryData, setInventoryData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // Fetch inventory data from API
  useEffect(() => {
    const fetchInventoryData = async () => {
      try {
        setLoading(true);
        setError('');
        const { data: products } = await api.get('/products');
        if (!Array.isArray(products)) throw new Error('Product response has an unexpected shape');

        const mappedProducts = products.map(product => {
          const currentStock = product.quantity === null || product.quantity === undefined || product.quantity === ''
            ? null
            : Number(product.quantity);
          const reorderPoint = product.reorderPoint === null || product.reorderPoint === undefined || product.reorderPoint === ''
            ? null
            : Number(product.reorderPoint);
          const locations = Array.isArray(product.locations) ? product.locations : [];
          const categories = Array.isArray(product.category) ? product.category : [product.category];
          return {
            id: product.id,
            name: product.name || 'N/A',
            sku: product.sku || 'N/A',
            category: categories.filter(Boolean).join(', ') || 'N/A',
            currentStock: Number.isFinite(currentStock) ? currentStock : null,
            reorderPoint: Number.isFinite(reorderPoint) ? reorderPoint : null,
            status: getStockStatus(currentStock, reorderPoint),
            lastUpdated: product.updatedAt || product.createdAt,
            location: locations[0] || 'N/A'
          };
        });

        const statusOrder = { critical: 0, low: 1, good: 2, unknown: 3 };
        mappedProducts.sort((a, b) => statusOrder[a.status] - statusOrder[b.status]);
        setInventoryData(mappedProducts);
      } catch (err) {
        console.error('Failed to fetch inventory data:', err);
        setInventoryData([]);
        setError('Inventory data could not be loaded from the backend.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchInventoryData();
  }, []);
  
  // Helper function to determine stock status
  const getStockStatus = (quantity, reorderPoint) => {
    if (!Number.isFinite(quantity) || !Number.isFinite(reorderPoint)) return 'unknown';
    if (quantity <= 0) return 'critical';
    if (quantity <= reorderPoint) return 'low';
    return 'good';
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      critical: { color: 'bg-error text-error-foreground', icon: 'AlertTriangle', label: 'Critical' },
      low: { color: 'bg-warning text-warning-foreground', icon: 'AlertCircle', label: 'Low Stock' },
      good: { color: 'bg-success text-success-foreground', icon: 'CheckCircle', label: 'Good' },
      unknown: { color: 'bg-muted text-muted-foreground', icon: 'CircleHelp', label: 'N/A' }
    };

    const config = statusConfig?.[status] || statusConfig.unknown;

    return (
      <div className={`inline-flex items-center space-x-1 px-2 py-1 rounded-full text-xs font-medium ${config?.color}`}>
        <Icon name={config?.icon} size={12} />
        <span>{config?.label}</span>
      </div>
    );
  };

  const handleSelectItem = (itemId) => {
    setSelectedItems(prev => 
      prev?.includes(itemId) 
        ? prev?.filter(id => id !== itemId)
        : [...prev, itemId]
    );
  };

  const handleSelectAll = () => {
    if (selectedItems?.length === inventoryData?.length) {
      setSelectedItems([]);
    } else {
      setSelectedItems(inventoryData?.map(item => item?.id));
    }
  };
  
  // Export inventory data to CSV
  const handleExport = () => {
    const headers = ['Product', 'SKU', 'Category', 'Current Stock', 'Reorder Point', 'Status', 'Location', 'Last Updated'];
    const data = inventoryData.map(item => [
      item.name,
      item.sku,
      item.category,
      item.currentStock,
      item.reorderPoint,
      item.status,
      item.location,
      formatUpdatedAt(item.lastUpdated)
    ]);
    
    exportDataToCSV(headers, data, 'inventory-status');
  };
  
  // Navigate to product details page for viewing
  const handleViewDetails = (productId) => {
    navigate(`/product-details/${productId}`);
  };
  
  // Handle stock adjustment
  const handleAdjustStock = (productId) => {
    navigate('/product-details', { state: { productId, activeTab: 'overview' } });
  };
  
  // Handle more options menu
  const handleMoreOptions = (productId) => {
    // For now, just navigate to product details
    navigate('/product-details', { state: { productId } });
  };
  
  // Handle bulk edit
  const handleBulkEdit = () => {
    // Navigate to product catalog with selected items
    navigate('/product-catalog', { state: { selectedItems } });
  };
  
  // Handle bulk stock adjustment
  const handleBulkAdjustStock = () => {
    // For now, just navigate to product catalog with selected items
    navigate('/product-catalog', { state: { selectedItems } });
  };

  return (
    <div className="bg-card border border-border rounded-lg shadow-card">
      <div className="p-4 sm:p-6 border-b border-border">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h2 className="text-base sm:text-lg font-semibold text-foreground">Inventory Status</h2>
            <p className="text-xs sm:text-sm text-muted-foreground">Items requiring attention</p>
          </div>
          <div className="flex items-center space-x-2">
            <Button 
              variant="outline" 
              iconName="Download" 
              iconPosition="left" 
              iconSize={16}
              onClick={handleExport}
              className="flex-1 sm:flex-none"
            >
              <span className="hidden sm:inline">Export</span>
            </Button>
            <Button 
              variant="default" 
              iconName="Plus" 
              iconPosition="left" 
              iconSize={16}
              onClick={() => navigate('/product-catalog')}
              className="flex-1 sm:flex-none"
            >
              <span className="hidden sm:inline">Add</span><span className="sm:hidden">Add</span>
            </Button>
          </div>
        </div>
      </div>
      
      {/* Desktop Table */}
      <div className="hidden lg:block overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted/50">
            <tr>
              <th className="text-left p-4">
                <input
                  type="checkbox"
                  checked={inventoryData.length > 0 && selectedItems?.length === inventoryData?.length}
                  onChange={handleSelectAll}
                  className="rounded border-border"
                />
              </th>
              <th className="text-left p-4 text-sm font-medium text-muted-foreground">Product</th>
              <th className="text-left p-4 text-sm font-medium text-muted-foreground">SKU</th>
              <th className="text-left p-4 text-sm font-medium text-muted-foreground">Stock</th>
              <th className="text-left p-4 text-sm font-medium text-muted-foreground">Status</th>
              <th className="text-left p-4 text-sm font-medium text-muted-foreground">Location</th>
              <th className="text-left p-4 text-sm font-medium text-muted-foreground">Updated</th>
              <th className="text-left p-4 text-sm font-medium text-muted-foreground">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={8} className="p-8 text-center text-muted-foreground">Loading inventory...</td></tr>
            ) : error ? (
              <tr><td colSpan={8} role="alert" className="p-8 text-center text-destructive">{error}</td></tr>
            ) : inventoryData.length === 0 ? (
              <tr><td colSpan={8} className="p-8 text-center text-muted-foreground">N/A — no inventory products found.</td></tr>
            ) : inventoryData.map((item) => (
              <tr key={item?.id} className="border-t border-border hover:bg-muted/30 transition-hover">
                <td className="p-4">
                  <input
                    type="checkbox"
                    checked={selectedItems?.includes(item?.id)}
                    onChange={() => handleSelectItem(item?.id)}
                    className="rounded border-border"
                  />
                </td>
                <td className="p-4">
                  <div>
                    <p className="font-medium text-foreground">{item?.name || 'N/A'}</p>
                    <p className="text-sm text-muted-foreground">{item?.category}</p>
                  </div>
                </td>
                <td className="p-4">
                  <span className="font-data text-sm text-foreground">{item?.sku || 'N/A'}</span>
                </td>
                <td className="p-4">
                  <div className="text-sm">
                    <span className="font-medium text-foreground">{item?.currentStock ?? 'N/A'}</span>
                    <span className="text-muted-foreground"> / {item?.reorderPoint ?? 'N/A'}</span>
                  </div>
                </td>
                <td className="p-4">
                  {getStatusBadge(item?.status)}
                </td>
                <td className="p-4">
                  <span className="text-sm text-foreground">{item?.location || 'N/A'}</span>
                </td>
                <td className="p-4">
                  <span className="text-sm text-muted-foreground">{formatUpdatedAt(item?.lastUpdated)}</span>
                </td>
                <td className="p-4">
                  <div className="flex items-center space-x-1">
                    <Button 
                      variant="ghost" 
                      size="icon" 
                      title="View Details"
                      onClick={() => handleViewDetails(item?.id)}
                    >
                      <Icon name="Eye" size={16} />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile Cards */}
      <div className="lg:hidden p-4 space-y-3">
        {loading ? (
          <p className="py-8 text-center text-muted-foreground">Loading inventory...</p>
        ) : error ? (
          <p role="alert" className="py-8 text-center text-destructive">{error}</p>
        ) : inventoryData.length === 0 ? (
          <p className="py-8 text-center text-muted-foreground">N/A — no inventory products found.</p>
        ) : inventoryData.map((item) => (
          <div key={item?.id} className="bg-card border border-border rounded-lg p-4 hover:bg-muted/30 transition-colors">
            <div className="flex items-start space-x-3 mb-3">
              <input
                type="checkbox"
                checked={selectedItems?.includes(item?.id)}
                onChange={() => handleSelectItem(item?.id)}
                className="rounded border-border mt-1"
              />
              <div className="flex-1 min-w-0">
                <h3 className="font-medium text-foreground truncate">{item?.name}</h3>
                <p className="text-xs text-muted-foreground">{item?.category}</p>
              </div>
            </div>
            
            <div className="mb-3">
              {getStatusBadge(item?.status)}
            </div>
            
            <div className="grid grid-cols-2 gap-3 mb-3">
              <div>
                <p className="text-xs text-muted-foreground mb-1">SKU</p>
                <span className="font-data text-sm text-foreground">{item?.sku || 'N/A'}</span>
              </div>
              <div>
                <p className="text-xs text-muted-foreground mb-1">Stock</p>
                <span className="font-medium text-foreground">{item?.currentStock ?? 'N/A'} / {item?.reorderPoint ?? 'N/A'}</span>
              </div>
              <div>
                <p className="text-xs text-muted-foreground mb-1">Location</p>
                <span className="text-sm text-foreground">{item?.location || 'N/A'}</span>
              </div>
              <div>
                <p className="text-xs text-muted-foreground mb-1">Updated</p>
                <span className="text-xs text-muted-foreground">{formatUpdatedAt(item?.lastUpdated)}</span>
              </div>
            </div>

            <div className="flex items-center justify-end">
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => handleViewDetails(item?.id)}
                iconName="Eye"
                iconPosition="left"
                iconSize={16}
              >
                View Details
              </Button>
            </div>
          </div>
        ))}
      </div>

      {selectedItems?.length > 0 && (
        <div className="p-4 bg-muted/30 border-t border-border">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <span className="text-sm text-foreground">
              {selectedItems?.length} item{selectedItems?.length > 1 ? 's' : ''} selected
            </span>
            <div className="flex items-center space-x-2">
              <Button 
                variant="outline" 
                size="sm" 
                iconName="Edit2" 
                iconPosition="left" 
                iconSize={14}
                onClick={handleBulkEdit}
                className="flex-1 sm:flex-none"
              >
                <span className="hidden sm:inline">Bulk Edit</span>
              </Button>
              <Button 
                variant="outline" 
                size="sm" 
                iconName="Package" 
                iconPosition="left" 
                iconSize={14}
                onClick={handleBulkAdjustStock}
                className="flex-1 sm:flex-none"
              >
                <span className="hidden sm:inline">Adjust Stock</span>
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InventoryStatusTable;


const formatUpdatedAt = (value) => {
  if (!value) return 'N/A';

  // Firestore Timestamp with toDate()
  if (value && typeof value.toDate === 'function') {
    const d = value.toDate();
    return formatDateString(d);
  }

  // Firestore Timestamp-like object { seconds, nanoseconds }
  if (value && typeof value.seconds === 'number') {
    const d = new Date(value.seconds * 1000);
    return formatDateString(d);
  }

  // Firestore Admin Timestamp serialized as {_seconds, _nanoseconds}
  if (value && typeof value._seconds === 'number') {
    const d = new Date(value._seconds * 1000);
    return formatDateString(d);
  }

  // Milliseconds since epoch
  if (typeof value === 'number') {
    const d = new Date(value);
    return !isNaN(d) ? formatDateString(d) : 'N/A';
  }

  // ISO string or Date
  if (typeof value === 'string' || value instanceof Date) {
    const d = new Date(value);
    if (!isNaN(d)) return formatDateString(d);
    return 'N/A';
  }

  // Fallback: try to construct
  try {
    const d = new Date(value);
    return !isNaN(d) ? formatDateString(d) : 'N/A';
  } catch {
    return 'N/A';
  }
};

const formatDateString = (date) => {
  // Build "26 October 2025 at 01:53:16 UTC+1" style
  const day = date.toLocaleString('en-GB', { day: '2-digit' });
  const month = date.toLocaleString('en-GB', { month: 'long' });
  const year = date.toLocaleString('en-GB', { year: 'numeric' });
  const time = date.toLocaleString('en-GB', { hour12: false, hour: '2-digit', minute: '2-digit', second: '2-digit' });

  const offsetMinutes = -date.getTimezoneOffset(); // minutes east of UTC
  const sign = offsetMinutes >= 0 ? '+' : '-';
  const hours = Math.floor(Math.abs(offsetMinutes) / 60);
  const mins = Math.abs(offsetMinutes) % 60;
  const tz = `UTC${sign}${hours}${mins ? ':' + String(mins).padStart(2, '0') : ''}`;

  return `${Number(day)} ${month} ${year} at ${time} ${tz}`;
};